import React, { useEffect, useState } from 'react';

import AllInclusiveIcon from '@mui/icons-material/AllInclusive';
import { Box, Chip, Paper, Stack, Typography } from '@mui/material';
import type { Theme } from '@mui/material/styles';

import { getLuxLoyaltyProgramConfig } from '~/services/LuxLoyaltyService';

import { luxLoyaltyBenefitDescriptionMap } from '~/utils/luxLoyalty';
import { formatBenefitValue, resolveBenefitDisplayValue } from '~/utils/luxLoyaltyBenefitValue';

import LuxLoyaltyBenefitIcon from './LuxLoyaltyBenefitIcon';

/** Rose gold accent for Infinite tier icons — matches customer portal `InfiniteLogo`. */
const INFINITE_ROSE_GOLD = '#F4C5A7';

/** Fixed tile height so every benefit card aligns in the grid. */
const BENEFIT_TILE_HEIGHT = 72;

/**
 * Explicit display order for benefits in the Société profile. Codes not in
 * this list fall to the end of the grid in their existing order.
 */
const BENEFIT_DISPLAY_ORDER: ReadonlyArray<App.LuxLoyaltyBenefitType> = [
  'airport_transfer',
  'hotel_upgrade',
  'lounge_pass',
  'family_and_friends_access',
  'esim_package',
  'tour_exp_credit',
  'travelshoot_credit',
  'discounted_insurance',
  'hidden_access',
  'early_access',
  'phone_support',
];

const BENEFIT_ORDER_INDEX: Partial<Record<App.LuxLoyaltyBenefitType, number>> = BENEFIT_DISPLAY_ORDER.reduce(
  (acc, code, idx) => {
    acc[code] = idx;
    return acc;
  },
  {} as Partial<Record<App.LuxLoyaltyBenefitType, number>>,
);

interface Props {
  account: App.LuxLoyaltyAccount;
}

function getTierPalette(theme: Theme, tier: App.LuxLoyaltyTier) {
  return theme.palette.loyaltyTiers[tier];
}

function getTierBenefitIconColor(tier: App.LuxLoyaltyTier, tierPalette: ReturnType<typeof getTierPalette>) {
  return tier === 'infinite' ? INFINITE_ROSE_GOLD : tierPalette.contrastText;
}

export default function LuxLoyaltyBenefitsOverview({ account }: Props) {
  const [programBenefits, setProgramBenefits] = useState<
    Awaited<ReturnType<typeof getLuxLoyaltyProgramConfig>>['benefits'] | undefined
  >();

  useEffect(() => {
    let cancelled = false;

    getLuxLoyaltyProgramConfig(account.region).then((config) => {
      if (!cancelled && config?.benefits) {
        setProgramBenefits(config.benefits);
      }
    });

    return () => {
      cancelled = true;
    };
  }, [account.region]);

  const benefits = [...account.benefits].sort((a, b) => {
    const aIsUnlimited = a.details.limit === Infinity;
    const bIsUnlimited = b.details.limit === Infinity;
    if (aIsUnlimited !== bIsUnlimited) {
      return aIsUnlimited ? 1 : -1;
    }

    const aIdx = BENEFIT_ORDER_INDEX[a.code] ?? Number.MAX_SAFE_INTEGER;
    const bIdx = BENEFIT_ORDER_INDEX[b.code] ?? Number.MAX_SAFE_INTEGER;
    return aIdx - bIdx;
  });

  if (benefits.length === 0) {
    return (
      <Typography variant="body2" color="text.secondary" sx={{ py: 4, textAlign: 'center' }}>
        No benefits available on this account.
      </Typography>
    );
  }

  return (
    <Box
      sx={{
        display: 'grid',
        gridTemplateColumns: { xs: '1fr', md: 'repeat(2, minmax(0, 1fr))' },
        gridAutoRows: `${BENEFIT_TILE_HEIGHT}px`,
        gap: 2,
      }}
    >
      {benefits.map((benefit) => {
        const { limit, remaining } = benefit.details;
        const isUnlimited = limit === Infinity;
        const isExhausted = !isUnlimited && remaining <= 0;
        const displayValue = resolveBenefitDisplayValue(programBenefits, benefit, account.tier);

        return (
          <Paper
            key={benefit.code}
            variant="outlined"
            sx={{
              p: 1.5,
              borderRadius: 2,
              height: '100%',
              display: 'flex',
              alignItems: 'center',
              overflow: 'hidden',
            }}
          >
            <Stack direction="row" spacing={1.25} alignItems="center" sx={{ minWidth: 0, width: '100%' }}>
              <Box
                sx={(theme) => {
                  const tierPalette = getTierPalette(theme, account.tier);

                  return {
                    width: 32,
                    height: 32,
                    borderRadius: '50%',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    flexShrink: 0,
                    background: tierPalette.gradient,
                    color: getTierBenefitIconColor(account.tier, tierPalette),
                  };
                }}
              >
                <LuxLoyaltyBenefitIcon benefitType={benefit.code} sx={{ fontSize: 18 }} />
              </Box>

              <Typography
                variant="body2"
                fontWeight={700}
                sx={{
                  flex: 1,
                  minWidth: 0,
                  lineHeight: 1.3,
                  display: '-webkit-box',
                  WebkitLineClamp: 2,
                  WebkitBoxOrient: 'vertical',
                  overflow: 'hidden',
                }}
              >
                {luxLoyaltyBenefitDescriptionMap.get(benefit.code) ?? benefit.code}
              </Typography>

              <Stack spacing={0.75} alignItems="flex-end" sx={{ flexShrink: 0, maxWidth: '42%' }}>
                {isUnlimited ? (
                  <Chip
                    size="small"
                    icon={<AllInclusiveIcon sx={{ fontSize: 14 }} />}
                    label="Unlimited"
                    sx={(theme) => {
                      const tierPalette = getTierPalette(theme, account.tier);

                      return {
                        height: 22,
                        fontWeight: 600,
                        color: tierPalette.contrastText,
                        background: tierPalette.gradient,
                        '& .MuiChip-icon': { color: 'inherit', ml: 0.5 },
                      };
                    }}
                  />
                ) : (
                  <Chip
                    size="small"
                    label={isExhausted ? 'Exhausted' : `${remaining}/${limit}`}
                    variant="outlined"
                    color={isExhausted ? 'warning' : 'default'}
                    sx={(theme) => {
                      const tierPalette = getTierPalette(theme, account.tier);

                      return {
                        height: 22,
                        fontWeight: 600,
                        ...(isExhausted
                          ? {}
                          : {
                              borderColor: tierPalette.dark,
                              color: theme.palette.text.primary,
                            }),
                      };
                    }}
                  />
                )}
                {displayValue && (
                  <Typography variant="caption" color="text.secondary" sx={{ lineHeight: 1.2, textAlign: 'right' }}>
                    {formatBenefitValue(displayValue, { includeEachSuffix: displayValue.unit === 'CURRENCY' })}
                  </Typography>
                )}
              </Stack>
            </Stack>
          </Paper>
        );
      })}
    </Box>
  );
}
